/* eslint-disable no-debugger */
import { useState, forwardRef, useRef, useEffect, useImperativeHandle } from "react";
import { saveAs } from 'file-saver';
import PropTypes from "prop-types";

// @mui material components
import {
  Container,
  Grid,
  Card,
  InputAdornment,
  CardMedia,
  CardActionArea,
  CardContent,
  Typography,
  LinearProgress,
  Chip,
  Button,
  CardActions,
  Skeleton,
  IconButton,
  Link,
} from "@mui/material";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

// @mui icons
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import InfoIcon from "@mui/icons-material/Info";
import DeleteIcon from "@mui/icons-material/Delete";

import PageHeaderContainer from "components/UI/PageHeaderContainer";
import InstructionsModal from "../components/InstructionsModal";
import RecordSignModal from "../components/RecordSignModal";
import TextSearchModal from "features/text-search/components/TextSearchModal";

import { useNotification } from "contextProviders/NotificationContext";
import { useSignSearchService } from "../sign-search-cloud.services";
import { usePersistentConfig } from "hooks/usePersistentConfig";

import SignVideo from "components/UI/SignVideo";
import LandmarkVideo from "components/UI/LandmarkVideo";


const ort = require('onnxruntime-web');


function SignSearch() {

  //const signSearchService = useSignSearchService();

  const notification = useNotification();

  const { hideRecordingInstructionsOnLoad, showRecordingDebugInfo } = usePersistentConfig();

  const instructionsModalRef = useRef(null);
  const recordSignModalRef = useRef(null);
  const textSearchModalRef = useRef(null);
  //const recordings = useRef([]);
  const [allInferenceResults, setAllInferenceResults] = useState([]);

  const [recordingsState, setRecordingsState] = useState([]);


  function normalizePose(data, bodyDict) {
    if (!data[bodyDict['pose_left_shoulder']] || !data[bodyDict['pose_right_shoulder']]) {
      return data;
    }

    //console.log('NORMALIZING POSE');
  
    //const leftShoulder = data[bodyDict['pose_left_shoulder']];
    //const rightShoulder = data[bodyDict['pose_right_shoulder']];
  
    //const shoulderDistance = Math.hypot(leftShoulder.x - rightShoulder.x, leftShoulder.y - rightShoulder.y);
    // //const midPoint = { x: (leftShoulder.x + rightShoulder.x) / 2, y: (leftShoulder.y + rightShoulder.y) / 2 };
    //const midPoint = { x: 0.5, y: 0.5 };
    //const headMetric = shoulderDistance / 2;
  
    //const startingPoint = [midPoint.x - 3 * headMetric, data[bodyDict['pose_right_eye']].y - (headMetric / 2)];
    //const endingPoint = [midPoint.x + 3 * headMetric, midPoint.y + 4.5 * headMetric];
    // //console.log('Starting Point:', startingPoint);
    // //console.log('Ending Point:', endingPoint);
    return data.map((kp) => {
      //if (kp.x === 0) return kp;
      //const normalizedX = (kp.x - startingPoint[0]) / (endingPoint[0] - startingPoint[0]) - 0.5;
      //const normalizedY = 1 - (kp.y - endingPoint[1]) / (startingPoint[1] - endingPoint[1]) - 0.5;
      //return { x: normalizedX, y: normalizedY };
      return { x: kp.x - 0.5, y: kp.y - 0.5};
    });
  }
  
  function normalizeHand(data) {
    // Extract X and Y values from the landmarks
    // const xValues = data.map(l => l.x);
    // const yValues = data.map(l => l.y);
  
    // Calculate width and height based on the range of X and Y values
    // const width = Math.max(...xValues) - Math.min(...xValues);
    // const height = Math.max(...yValues) - Math.min(...yValues);
  
    // let deltaX, deltaY;
  
    // // Calculate deltas based on whether width or height is greater
    // if (width > height) {
    //   deltaX = 0.1 * width;
    //   deltaY = deltaX + ((width - height) / 2);
    // } else {
    //   deltaY = 0.1 * height;
    //   deltaX = deltaY + ((height - width) / 2);
    // }
  
    // // Set the starting and ending points for normalization
    // const startingPoint = { 
    //   x: Math.min(...xValues) - deltaX, 
    //   y: Math.min(...yValues) - deltaY 
    // };
    // const endingPoint = { 
    //   x: Math.max(...xValues) + deltaX, 
    //   y: Math.max(...yValues) + deltaY 
    // };
  
    // Normalize each landmark
    return data.map((kp, pos) => {
      // Skip normalization if any values are zero or if the bounding box has zero width/height
      // if (kp.x === 0 || kp.y === 0 || (endingPoint.x - startingPoint.x) === 0 || (endingPoint.y - startingPoint.y) === 0) {
      //   return kp;
      // }
  
      //const normalizedX = (kp.x - startingPoint.x) / (endingPoint.x - startingPoint.x);
      //const normalizedY = (kp.y - startingPoint.y) / (endingPoint.y - startingPoint.y);
  
      return {
        x: kp.x - 0.5,
        y: kp.y - 0.5,
      };
    });
  }

  
    // Function to calculate the chest_middle_up keypoint
  function calculateChestMiddleUp(poseLandmarks) {
    const leftShoulder = poseLandmarks[11];  // Left shoulder keypoint
    const rightShoulder = poseLandmarks[12]; // Right shoulder keypoint
  
    if (!leftShoulder || !rightShoulder) {
      console.error('Shoulder keypoints missing');
      return null;
    }

    // Calculate midpoint between the left and right shoulders
    const chestMiddleUp = {
        x: (leftShoulder.x + rightShoulder.x) / 2,
        y: (leftShoulder.y + rightShoulder.y) / 2,
        z: (leftShoulder.z + rightShoulder.z) / 2,
        visibility: (leftShoulder.visibility + rightShoulder.visibility) / 2  // Optional if visibility is used
    };

    return chestMiddleUp;
  }

  // Function to filter landmarks by selected keypoints with error handling
  function filterLandmarks(landmarksArray, selectedKeypoints) {
    try {
      if (!Array.isArray(landmarksArray)) {
          throw new Error("Invalid landmarks array.");
      }
  
      // filter based on selected indices
      return landmarksArray.filter((_, index) => selectedKeypoints.includes(index));
    } catch (error) {
      console.error(`Error filtering landmarks: ${error.message}`);
      return [];  // Return an empty array if there's an error
    }
  }


  function flattenArray(dataArray) {
    // Calculate total elements (39 * 54 * 2) since you have 39 arrays, each with 54 objects, each with 2 values (x, y)
    const totalElements = dataArray.length * dataArray[0].length * 2;
    const float32Array = new Float32Array(totalElements);
    let index = 0;

    for (let i = 0; i < dataArray.length; i++) {
        for (let j = 0; j < dataArray[i].length; j++) {
            const point = dataArray[i][j];
            if (point && typeof point.x === 'number' && typeof point.y === 'number') {
                float32Array[index++] = point.x;
                float32Array[index++] = point.y;
            } else {
                console.warn(`Invalid point at [${i}][${j}]`);
                float32Array[index++] = 0;
                float32Array[index++] = 0;
            }
        }
    }

    return float32Array;
  }

  function getargmax(dataArray) {
    return dataArray.indexOf(Math.max(...dataArray));
  }

  const processRecordedSign = async (data, meanings, file_name, duration_mp) => {
    const startTime = performance.now();

    const defaultLandmark = { x: 0, y: 0, z: 0, visibility: 0 };
  
    const fillMissingLandmarks = (landmarks, requiredSize, fallbackLandmark) => {
      const filledLandmarks = [...landmarks];
      while (filledLandmarks.length < requiredSize) {
        filledLandmarks.push(fallbackLandmark || defaultLandmark);
      }
      return filledLandmarks;
    };
  
    const isLandmarkNaN = (landmark) => {
      return isNaN(landmark.x) || isNaN(landmark.y) || isNaN(landmark.z);
    };
  
    const selectedPoseKeypoints = [0, 2, 5, 7, 8, 11, 12, 13, 14, 15, 16, 33];
  
    const keypoints = data.landmarks.map(l => {
      //console.log('Landmarks:', l);
      let poseLandmarks = l.poseLandmarks ? [...l.poseLandmarks] : [];
      if (!poseLandmarks.length) {
        poseLandmarks = Array(33).fill(defaultLandmark);
      }
  
      const chestMiddle = calculateChestMiddleUp(poseLandmarks) || defaultLandmark;
      poseLandmarks.push(chestMiddle);
      //console.log('Pose Landmarks:', poseLandmarks);
  
      let leftHandLandmarks = l.leftHandLandmarks ? [...l.leftHandLandmarks] : [];
      if (!leftHandLandmarks.length) {
        leftHandLandmarks = fillMissingLandmarks(leftHandLandmarks, 21, poseLandmarks[15]);
      }
  
      let rightHandLandmarks = l.rightHandLandmarks ? [...l.rightHandLandmarks] : [];
      if (!rightHandLandmarks.length) {
        rightHandLandmarks = fillMissingLandmarks(rightHandLandmarks, 21, poseLandmarks[16]);
      }    
  
      const normalizedPose = normalizePose(poseLandmarks, { 'pose_left_shoulder': 11, 'pose_right_shoulder': 12, 'pose_right_eye': 5 });
      const normalizedLeftHand = normalizeHand(leftHandLandmarks);
      const normalizedRightHand = normalizeHand(rightHandLandmarks);

      const filteredNormalizedPoseLandmarks = fillMissingLandmarks(
        filterLandmarks(normalizedPose, selectedPoseKeypoints),
        selectedPoseKeypoints.length
      );
  
      return {
        pose_landmarks: filteredNormalizedPoseLandmarks.map(({ x, y }) => ({ x, y })),
        left_hand_landmarks: normalizedLeftHand.map(({ x, y }) => ({ x, y })),
        right_hand_landmarks: normalizedRightHand.map(({ x, y }) => ({ x, y }))
      };
    });

    const concatenatedKeypoints = keypoints.map(k => [
      ...k.pose_landmarks,
      ...k.left_hand_landmarks,
      ...k.right_hand_landmarks
    ]);
  
    const adjustedKeypoints = concatenatedKeypoints.map(keypoints => {
      const [removed] = keypoints.splice(11, 1);
      keypoints.push(removed);
      return keypoints;
    });

    try {
      // Invoke the SageMaker Lambda function through the API
      const signSearchService = useSignSearchService();
      const response = await signSearchService.uploadLandmarks(adjustedKeypoints) //(data.landmarks); // Upload landmarks for inference
      console.log("Response from Lambda:", response);

      if (!response || !Array.isArray(response)) {
        throw new Error("No response from the Lambda service or invalid format");
      }
    
      // Extract top 5 predictions (if the length exceeds 5)
      const top5Predictions = response.slice(0, 5);
    
      // Create predictions list from gloss or word
      const predictions = top5Predictions.map((pred) => pred.word || `Unknown (${pred.gloss})`);
      data.signs = predictions; // Store predictions in the `data.signs` field
    
      console.log(`Predictions: ${predictions}`);
    
      // Save inference metadata
      const endTime = performance.now();
      setAllInferenceResults((prevResults) => [
        ...prevResults,
        {
          fileName: file_name,
          predictions,
          duration_mp, // Duration of media playback
          duration_inf: endTime - startTime, // Inference duration
        },
      ]);
    
      // Update recording state
      setRecordingsState((prevState) =>
        prevState.map((r) => (r.uuid === data.uuid ? { ...r, signs: predictions, processingSigns: false } : r))
      );
    } catch (error) {
      console.error("Error during sign processing:", error);
      data.error = "An error occurred while processing the sign.";
      data.processingSigns = false;
    
      // Optional notification system (if needed)
      /* notification.open({
         title: "Error",
         subTitle: "Could not process the sign",
         type: "error",
      }); */
    } finally {
      // Ensure the processing state is cleared
      data.processingSigns = false;
    }
  };

  const onRecorded = (data, meanings, file_name, duration_mp) => {
    //console.log(data)
    data.processingSigns = true;
    data.signs = [];
    //recordings.current.unshift(data);
    //setRecordingsState([data, ...recordingsState]);
    setRecordingsState((prevRecordings) => [data, ...prevRecordings]);
    processRecordedSign(data, meanings, file_name, duration_mp);
  };

  const removeRecording = (recording) => {
    //alert("Eliminado");
    //debugger;
    //recordings.current = recordings.current.filter((x) => x.uuid != recording.uuid);
    setRecordingsState((prevState) => prevState.filter((x) => x.uuid != recording.uuid));
  };

  useEffect(() => {
    if (!hideRecordingInstructionsOnLoad) {
      instructionsModalRef.current.showModal(true, false, true);
    }
  }, [instructionsModalRef]);


  const handleDownloadResults = () => {
    if (allInferenceResults.length === 0) {
      console.error("No hay resultados para descargar.");
      return;
    }
  
    // Generate CSV content
    const csvContent =
      "video,Predictions,Duration_mp,Duration_inf\n" +
      allInferenceResults
        .map(
          ({ fileName, predictions, duration_mp, duration_inf }) =>
            `${fileName},"${predictions.join(", ")}",${duration_mp || "N/A"},${duration_inf || "N/A"}`
        )
        .join("\n");
  
    // Save CSV file
    const blobCsv = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blobCsv, "resultados_inferencias.csv");
  
  };
  

  return (
    <>
      <InstructionsModal
        ref={instructionsModalRef}
        onStartRecordingHandler={() => {
          recordSignModalRef.current.showModal(true);
        }}
      ></InstructionsModal>
      <RecordSignModal ref={recordSignModalRef} onRecorded={onRecorded}></RecordSignModal>
      <TextSearchModal ref={textSearchModalRef}></TextSearchModal>

      <PageHeaderContainer>
        <MKTypography
          variant="h1"
          color="white"
          mt={-6}
          mb={1}
          sx={({ breakpoints, typography: { size } }) => ({
            [breakpoints.down("md")]: {
              fontSize: size["3xl"],
            },
          })}
        >
          Búsqueda por Seña 
        </MKTypography>
        <MKTypography
          variant="body1"
          color="white"
          textAlign="center"
          px={{ xs: 6, lg: 12 }}
          mt={1}
          mb={5}
        >
          Sube tus grabaciones para intepretar una seña. <br />
        </MKTypography>
        <div>
          <MKButton
            size="large"
            onClick={() => recordSignModalRef.current.showModal(true)}
            sx={{ mb: 1 }}
          >
            <RadioButtonCheckedIcon fontSize="medium" className="countdown-pulse" sx={{ mr: 1 }} />
            Subir Videos
          </MKButton>
          <MKButton
            size="large"
            onClick={handleDownloadResults}
            sx={{ ml: { xs: 0, sm: 1 }, mb: 1 }}
          >
            <InfoIcon fontSize="medium" sx={{ mr: 1 }} />
            Descargar resultados
          </MKButton>
        </div>
      </PageHeaderContainer>
      <Container>
        <Grid container spacing={2} sx={{ justifyContent: "center" }}>
          {recordingsState.map((r, i) => (
            <Grid item xs={12} md={6} lg={4} key={r.uuid}>
              <Card sx={{}}>
                <SignVideo
                  source={r.mediaUrl}
                  style={{ borderRadius: "0.75rem 0.75rem 0 0" }}
                ></SignVideo>

                <IconButton
                  style={{ position: "absolute", right: "10px", top: "10px" }}
                  onClick={() => removeRecording(r)}
                >
                  <DeleteIcon></DeleteIcon>
                </IconButton>
                {r.processingSigns ? (
                  <LinearProgress
                    variant="indeterminate"
                    value={true}
                    sx={{ width: "100%", overflow: "hidden", height: 4 }}
                  ></LinearProgress>
                ) : (
                  <MKBox sx={{ width: "100%", overflow: "hidden", height: 4 }}></MKBox>
                )}
                <CardContent>
                  <Typography gutterBottom variant="h5" component="div">
                    {r.processingSigns ? "Cargando resultados..." : "Resultados"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {r.error && <MKBox color={"error"}>{r.error}</MKBox>}

                    {r.processingSigns ? (
                      <MKBox>
                        <Skeleton variant="text" sx={{ width: "60%" }}>A</Skeleton>
                        <Skeleton variant="text" sx={{ width: "40%" }}>B</Skeleton>
                        <Skeleton variant="text" sx={{ width: "50%" }}>C</Skeleton>
                        <Skeleton variant="text" sx={{ width: "70%" }}></Skeleton>
                        <Skeleton variant="text" sx={{ width: "40%" }}></Skeleton>
                      </MKBox>
                    ) : (
                      <MKBox sx={{ pl: 2 }}>
                      <ul>
                        {r.signs.map((s, idx) => (
                          <li key={idx}>
                            <a href={`#${s}`}>
                              {s}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </MKBox>
                    )}
                  </Typography>
                </CardContent>
                {showRecordingDebugInfo && (
                  <>
                    <CardActions style={{ justifyContent: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        Landmarks: {r.landmarks.length} | Media chunks: {r.media.length}
                      </Typography>
                    </CardActions>
                    <LandmarkVideo
                      frames={r.frames}
                      landmarks={r.landmarks}
                      style={{ borderRadius: "0.75rem 0.75rem 0 0" }}
                    ></LandmarkVideo>
                  </>
                )}
                {false && (
                  <CardActions>
                    <Button size="small">Botón A</Button>
                    <Button size="small">Botón B</Button>
                  </CardActions>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </>
  );
}

export default SignSearch;